import React, { useState, useRef, useEffect } from 'react';

interface SliderProps {
  value: number[];
  onValueChange: (value: number[]) => void;
  max?: number;
  min?: number;
  step?: number;
  className?: string;
  disabled?: boolean;
}

export const Slider: React.FC<SliderProps> = ({
  value,
  onValueChange,
  max = 100,
  min = 0,
  step = 1,
  className = '',
  disabled = false,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const sliderRef = useRef<HTMLDivElement>(null);
  const currentValue = value[0] || 0;

  const percentage = ((currentValue - min) / (max - min)) * 100;

  const handleMouseDown = (e: React.MouseEvent) => {
    if (disabled) return;
    setIsDragging(true);
    updateValue(e.clientX);
  };

  const updateValue = (clientX: number) => {
    if (!sliderRef.current || disabled) return;

    const rect = sliderRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    const percentage = x / rect.width;
    const rawValue = min + (max - min) * percentage;
    
    // Round to step
    const steppedValue = Math.round(rawValue / step) * step;
    const clampedValue = Math.max(min, Math.min(max, steppedValue));
    
    onValueChange([clampedValue]);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        updateValue(e.clientX);
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  return (
    <div
      ref={sliderRef}
      className={`relative flex w-full touch-none select-none items-center ${className}`}
      onMouseDown={handleMouseDown}
    >
      <div className={`relative h-2 w-full grow overflow-hidden rounded-full bg-gray-200 ${disabled ? 'opacity-50' : ''}`}>
        <div
          className="absolute h-full bg-blue-600"
          style={{ width: `${percentage}%` }}
        />
      </div>
      <div
        className={`
          absolute block h-5 w-5 rounded-full border-2 border-blue-600 bg-white 
          ring-offset-white transition-colors focus-visible:outline-none 
          focus-visible:ring-2 focus-visible:ring-gray-950 focus-visible:ring-offset-2 
          ${disabled ? 'cursor-not-allowed' : 'cursor-grab'}
          ${isDragging ? 'cursor-grabbing' : ''}
        `}
        style={{ left: `calc(${percentage}% - 10px)` }}
      />
    </div>
  );
};